import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { cn } from "@/lib/utils";
import { createTouchOpenAutoFocusHandler } from "@/lib/touchAutoFocus";
import {
  SHEET_CLOSE_CLASS,
  SheetCloseIcon,
  SheetGrabZone,
  SHEET_ANIM_CENTERED,
  SHEET_ANIM_RISE,
  SHEET_GRAB_ZONE_CLASS,
  SHEET_OVERLAY_CLASS,
  type SheetFit,
} from "@/components/ui/sheetChrome";
import { useSheetFitClass } from "@/hooks/use-sheet-fit";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      SHEET_OVERLAY_CLASS,
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideCloseButton?: boolean;
    /** Sube desde abajo en móvil, como el parte de aplicación. */
    rise?: boolean;
    /** Pinta el tirador canónico (arrastrable) en móvil. Para hojas ancladas abajo. */
    handle?: boolean;
    /**
     * Cuánto ocupa en el teléfono (ver `SheetFit`). Solo hace algo con `rise`:
     * sin él esto no es una hoja inferior, es una ventana centrada, y estirarla
     * hasta arriba no significa nada. Aquí el que se desplaza es el PANEL, así
     * que se mide él mismo.
     */
    fit?: SheetFit;
  }
>(({ className, children, hideCloseButton, rise, handle, fit = "auto", onOpenAutoFocus, ...props }, ref) => {
  const grabCloseRef = React.useRef<HTMLButtonElement>(null);
  const [panel, setPanelNode] = React.useState<HTMLDivElement | null>(null);
  const setPanel = React.useCallback(
    (node: HTMLDivElement | null) => {
      setPanelNode(node);
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [ref],
  );
  const fitClass = useSheetFitClass({
    fit: rise ? fit : "short",
    host: panel,
  });
  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={setPanel}
      onOpenAutoFocus={createTouchOpenAutoFocusHandler(onOpenAutoFocus)}
      className={cn(
        /* `[&>*]:min-w-0`: esto es un GRID, y un hijo de grid arranca con
           `min-width: auto`, o sea «tan ancho como mi contenido». Basta un nombre
           largo sin espacios —o cualquier `truncate`, que mete `white-space: nowrap`—
           para que el hijo mida más que el diálogo, se salga por la derecha y lo que
           haya después (un botón, media frase) quede fuera de la pantalla, recortado
           por el `overflow-x-hidden`. Con min-w-0 el hijo puede encoger y el
           `truncate`/`break-words` de dentro hace su trabajo. */
        "fixed left-[50%] top-[50%] z-50 grid [&>*]:min-w-0 w-[calc(100vw-2rem)] max-w-lg max-h-[calc(100vh-4rem)] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-primary-foreground p-6 shadow-lg rounded-[24px] overflow-y-auto overflow-x-hidden overscroll-contain",
            rise ? SHEET_ANIM_RISE : SHEET_ANIM_CENTERED,
        className,
        // AL FINAL, después del `className` del consumidor: mismo grupo de
        // tailwind-merge, gana la última. Ver `useSheetFitClass`.
        fitClass,
      )}
      style={{ touchAction: "pan-y" }}
      {...props}
    >
      {handle && (
        <>
          <SheetGrabZone
            onClose={() => grabCloseRef.current?.click()}
            className={SHEET_GRAB_ZONE_CLASS}
          />
          <DialogPrimitive.Close ref={grabCloseRef} className="hidden" aria-hidden tabIndex={-1} />
        </>
      )}
      {children}
      {!hideCloseButton && (
        <DialogPrimitive.Close className={cn("absolute right-4 top-4", SHEET_CLOSE_CLASS)}>
          <SheetCloseIcon />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

// El `pr` reserva el hueco de la X (34 px + 12 de aire): la cabecera no es hermana
// flex del botón, que va en `absolute`, así que sin esto un título largo se le mete
// por debajo. Solo en sm+: en móvil el título va centrado y no llega al botón.
const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left sm:pr-[46px]", className)}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-xl font-semibold leading-none tracking-tight text-foreground", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
